import { PostHog } from "posthog-js";

import { AnalyticsService } from "../../context/Analytics/AnalyticsProvider";
import withQueue from "../analytics/withQueue";
import getLegacyAnonymousId from "../analytics/getLegacyAnonymousId";
import { consentClient } from "../cookie-consent/consentClient";
import { ServicePolicyMap } from "../cookie-consent/ServicePolicyMap";

import { getPosthogInitConfig } from "./getPosthogInitConfig";

export type PosthogDistinctId = string;
export type MaybeDistinctId = string | null;

export type PosthogConfig = {
  apiKey: string;
  apiHost: string;
  uiHost?: string;
};

export const posthogToAnalyticsServiceWithoutQueue = (
  client: PostHog,
): AnalyticsService<PosthogConfig> => ({
  name: "posthog",
  init: ({ apiKey, apiHost, uiHost }) =>
    new Promise((resolve) => {
      if (!client.__loaded) {
        client.init(
          apiKey,
          getPosthogInitConfig({
            apiHost,
            uiHost,
            loaded: () => {
              const legacyAnonymousId = getLegacyAnonymousId();
              if (legacyAnonymousId) {
                client.register({
                  legacy_anonymous_id: legacyAnonymousId,
                });
              }

              resolve(client.get_distinct_id());
            },
          }),
        );
      } else {
        resolve(client.get_distinct_id());
      }
    }),
  identify: (userId, properties) => {
    client.identify(userId, properties);
  },
  alias: (aliasId, userId) => {
    client.alias(aliasId, userId);
  },
  getSessionId: () => {
    return client.get_session_id();
  },
  page: () => {
    client.capture("$pageview");
  },
  trackFeatureFlag: (properties) => {
    client.capture("$feature_flag_called", properties);
  },
  track: (name, properties, options) => {
    const m = window.location.search.match(/update_tracking_profile=true/);

    if ((name === "$pageview" || name === "$autocapture") && m) {
      client.capture("$set", {
        $set: {
          latest_search_params: window.location.search,
        },
      });
    }
    client.capture(
      name,
      properties,
      options?.sendInstantly
        ? { send_instantly: true, transport: "sendBeacon" }
        : undefined,
    );
  },
  optIn: () => {
    if (client.has_opted_out_capturing()) {
      client.opt_in_capturing();
    }
  },
  optOut: () => {
    // Reset PostHog back to its default "pending" consent state. In
    // `cookieless_mode: "on_reject"` that prevents capture, while avoiding the
    // internal `$pageview` emitted by `opt_out_capturing()`.
    client.clear_opt_in_out_capturing();
  },
  state: () => {
    switch (consentClient.getConsent(ServicePolicyMap.POSTHOG)) {
      case "denied":
        return "disabled";
      case "granted":
        return "enabled";
      default:
        return "pending";
    }
  },
});

export default (client: PostHog) =>
  withQueue(posthogToAnalyticsServiceWithoutQueue(client));
